/**
 * BIP39 recovery phrases for Cobalt Wallet.
 *
 * Security model:
 * - The phrase (12 or 24 words) is generated locally with crypto.getRandomValues
 *   through @scure/bip39. It never leaves the device.
 * - The optional "25th word" is a BIP39 passphrase: it is NOT stored anywhere.
 *   Without it the same phrase derives a different, empty account, which gives
 *   plausible deniability and protects a stolen paper backup.
 * - The phrase + optional 25th word deterministically derive the XRPL family
 *   seed, so the same inputs always restore the same account.
 */

import { generateMnemonic, mnemonicToSeedSync, validateMnemonic } from "@scure/bip39";
import { wordlist } from "@scure/bip39/wordlists/english.js";

export type MnemonicLength = 12 | 24;

export function createMnemonic(length: MnemonicLength = 24): string {
  return generateMnemonic(wordlist, length === 24 ? 256 : 128);
}

export function normalizeMnemonic(input: string): string {
  return input.trim().toLowerCase().split(/\s+/u).join(" ");
}

export function mnemonicWords(input: string): string[] {
  const norm = normalizeMnemonic(input);
  return norm ? norm.split(" ") : [];
}

export function isValidMnemonic(input: string): boolean {
  const norm = normalizeMnemonic(input);
  const count = norm ? norm.split(" ").length : 0;
  if (count !== 12 && count !== 24) return false;
  return validateMnemonic(norm, wordlist);
}

/** Words that are not in the BIP39 English wordlist — surfaced while typing. */
export function unknownWords(input: string): string[] {
  const set = new Set(wordlist);
  return mnemonicWords(input).filter((w) => !set.has(w));
}

export function assertExtraWord(extraWord: string | undefined) {
  if (!extraWord) return;
  if (/\s/u.test(extraWord)) {
    throw new Error("The 25th word cannot contain spaces.");
  }
  if (extraWord.length > 64) {
    throw new Error("The 25th word must be 64 characters or fewer.");
  }
}

/**
 * Derives the XRPL account from a phrase + optional 25th word.
 * 16 bytes of the BIP39 seed become the XRPL family-seed entropy.
 */
export async function accountFromMnemonic(
  mnemonic: string,
  extraWord?: string,
): Promise<{ seed: string; address: string }> {
  const norm = normalizeMnemonic(mnemonic);
  if (!isValidMnemonic(norm)) throw new Error("That recovery phrase is not valid.");
  assertExtraWord(extraWord?.trim() || undefined);

  const seedBytes = mnemonicToSeedSync(norm, extraWord?.trim() ?? "");
  const entropy = seedBytes.slice(0, 16);

  const { Wallet } = await import("xrpl");
  const wallet = Wallet.fromEntropy(entropy);
  if (!wallet.seed) throw new Error("Key derivation failed.");
  return { seed: wallet.seed, address: wallet.address };
}
